import usePlayerControls from '@omni/kit/components/KitPlayer/components/Controls/hooks/usePlayerControls';
import Spacing from '@omni/kit/theming/Spacing';
import React, { forwardRef, useMemo } from 'react';
import { Pressable, StyleSheet, View, ViewStyle } from 'react-native';

import ActionIndicator from '../../components/ActionIndicator';
import BasicInfo from '../../components/BasicInfo';
import ControlsBackground from '../../components/ControlsBackground';
import LiveIndicator from '../../components/LiveStatus';
import { PlayerControlsProps, PlayerControlsRef } from '../../types';
import { PlayerControlsDefinitions } from '../types';
import { defaultControls } from './TvControls.types';
import PlayPauseControl from './components/PlayPauseControl';
import { PlayerButtonSizes } from './components/PlayerButton';
import TvPlayerTimeline from './components/PlayerTimeline/TvPlayerTimeline';

const TVPlayerControls = forwardRef<PlayerControlsRef, PlayerControlsProps>(
  (
    {
      actionIndicatorRef,
      analyticsFormat = 'video',
      controlOverrides,
      data,
      isLive,
      isLoading,
      isPlaying = false,
      playerTimelineRef,
      playerVideoRef,
      playId = '',
      totalDurationSeconds,

      onTogglePlay,
      onSeekTo,
      onFastSeekStart,
      onFastSeekEnd,
    },
    playerControlsRef
  ) => {
    const controls: PlayerControlsDefinitions = useMemo(
      () => ({
        ...defaultControls,
        actionIndicator: !isLive,
        timeline: !isLive,
        centeredPlayPause: isLive,
        ...controlOverrides,
      }),
      [controlOverrides, isLive]
    );

    const {
      areControlsVisible,
      handleContainerPress,
      fastSeekMultiplierText,
      isTimelineOnlyVisible,
      isFastSeeking,
      fastSeekDirection,
      FocusedControl,

      handleTogglePlay,
    } = usePlayerControls({
      actionIndicatorRef,
      analyticsFormat,
      appKey: data.appKey,
      controls,
      isLoading,
      isPlaying,
      playerControlsRef,
      playerTimelineRef,
      playerVideoRef,
      playId,
      totalDurationSeconds,

      onFastSeekEnd,
      onFastSeekStart,
      onSeekTo,
      onTogglePlay,
    });

    return (
      <>
        <Pressable
          focusable={false}
          isTVSelectable={false}
          style={[
            StyleSheet.absoluteFill,
            areControlsVisible
              ? styles.controlsContainerWithControlsVisible
              : styles.controlsContainerWithControlsHidden,
          ]}
          onPress={handleContainerPress}
        >
          <View style={styles.controlsContainer}>
            <ControlsBackground
              isVisible={areControlsVisible || isTimelineOnlyVisible}
              type={controls.backgroundType}
            />

            <View style={styles.topControls}>
              {controls.basicInfo ? (
                <BasicInfo
                  isVisible={areControlsVisible}
                  title={data.title}
                  subtitle={data.subtitle}
                />
              ) : null}
              {controls.liveStatus && isLive ? (
                <LiveIndicator
                  isVisible={areControlsVisible}
                  style={{ marginLeft: 'auto' }}
                />
              ) : null}
            </View>

            <View style={styles.centerControls}>
              {controls.centeredPlayPause && !isLoading ? (
                <PlayPauseControl
                  isVisible={areControlsVisible}
                  size={PlayerButtonSizes.large}
                  isPlaying={isPlaying}
                  onPress={() => handleTogglePlay?.()}
                  onFocus={() => (FocusedControl.current = 'playPause')}
                  onBlur={() => (FocusedControl.current = undefined)}
                  trapFocusDirections={controls.playButtonTrapFocusDirections}
                />
              ) : null}
            </View>

            <View style={[styles.bottomControls]}>
              {controls.playPause && !controls.centeredPlayPause ? (
                <PlayPauseControl
                  isVisible={areControlsVisible}
                  isPlaying={isPlaying}
                  onPress={() => handleTogglePlay?.()}
                  onFocus={() => (FocusedControl.current = 'playPause')}
                  onBlur={() => (FocusedControl.current = undefined)}
                  trapFocusDirections={controls.playButtonTrapFocusDirections}
                />
              ) : null}
              {controls.timeline ? (
                <TvPlayerTimeline
                  ref={playerTimelineRef}
                  isVisible={areControlsVisible || isTimelineOnlyVisible}
                  isShowingTooltipTime={
                    !areControlsVisible ? isTimelineOnlyVisible : undefined
                  }
                  isFastSeeking={isFastSeeking}
                  fastSeekDirection={fastSeekDirection}
                  fastSeekMultiplierText={fastSeekMultiplierText}
                  totalDurationSeconds={totalDurationSeconds}
                  playerVideoRef={playerVideoRef}
                />
              ) : null}
            </View>
          </View>
        </Pressable>

        <View
          style={[StyleSheet.absoluteFill, CENTER_FLEX]}
          pointerEvents='none'
        >
          <ActionIndicator
            ref={actionIndicatorRef}
            isLoading={isLoading}
            onlyShowLoading={!controls.actionIndicator}
          />
        </View>
      </>
    );
  }
);

const CENTER_FLEX: ViewStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const styles = StyleSheet.create({
  controlsContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingVertical: Spacing.xxxl,
    paddingHorizontal: 64,
    height: '100%',
    position: 'relative',
    width: '100%',
  },
  controlsContainerWithControlsVisible: { cursor: 'initial' } as any,
  controlsContainerWithControlsHidden: { cursor: 'none' } as any,
  topControls: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 'auto',
    width: '100%',
  },
  centerControls: {
    ...CENTER_FLEX,
    flexDirection: 'row',
    alignSelf: 'center',
  },
  bottomControls: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    marginTop: 'auto',
    gap: Spacing.xl,
    marginBottom: Spacing.l,
    paddingRight: 78,
    width: '100%',
  },
});

export default TVPlayerControls;
